import { snakeCase, intersection } from 'lodash'

import { TargetType } from 'components/attacks/create/create-attack-types'
import { generateAttackConfiguration } from 'components/attacks/create/create-attack-utils'
import { getSelectedKubernetesTargets } from 'components/attacks/create/targets/kubernetes/kubernetes-target-utils'
import {
  convertParamsToSets,
  getTotalSelectedClients,
  isTargetParamsEmpty,
  getTargetParamsKey
} from 'components/attacks/create/targets/target-utils'
import {
  getGraphNodesByType,
  graphNodeToStepAttack
} from 'components/scenarios/utils/graph'
import {
  isActive,
  isComplete
} from 'components/scenarios/utils/graph-node-lifecycle'
import { trans } from 'i18n/translate'
import theme from 'theme'
import { InfraStrategyType } from 'types/api/attacks/details/targeting'
import {
  ScenarioGraphNodeType,
  StatusCheckNode
} from 'types/api/scenarios/scenario'
import { naturalSortBy, unique } from 'utils/list'
import { cleanObjKeysAndValues, keysToSnakeCase } from 'utils/obj'

import type { AttackSchedule } from 'types/api/schedules/active'
import type { ScenarioSchedule } from 'types/api/schedules/scenarios/scenario-schedule'

const countableNodeTypes = [
  ScenarioGraphNodeType.InfraAttack,
  ScenarioGraphNodeType.SynchronousStatusCheck
]

export const stepTypeLabels = {
  [ScenarioGraphNodeType.InfraAttack]: 'Experiment',
  [ScenarioGraphNodeType.ContinuousStatusCheck]: trans(
    'status_checks.continuous.label'
  ),
  [ScenarioGraphNodeType.SynchronousStatusCheck]: trans(
    'status_checks.label.singular'
  )
}

export function getScheduleMaxRuns(
  schedule: ScenarioSchedule | AttackSchedule
) {
  return schedule.trigger.maxRuns && Number(schedule.trigger.maxRuns)
}

export function getScenarioStepCount(scenario) {
  const countableNodes = getGraphNodesByType(
    scenario.graph,
    ...countableNodeTypes
  )
  return countableNodes.length
}

export function getCurrentStepForRun(run) {
  if (run) {
    const steps = getGraphNodesByType(run.graph, ...countableNodeTypes)
    const completedNodes = steps
      .map(
        (step) =>
          isActive(step.state?.lifecycle) || isComplete(step.state?.lifecycle)
      )
      .filter((successful) => successful === true)

    return completedNodes.length
  }
  return 0
}

export function getFirstAttackAsStep(scenario) {
  const attacks = getGraphNodesByType(
    scenario?.graph,
    ScenarioGraphNodeType.InfraAttack
  )
  if (!attacks.length) return
  return graphNodeToStepAttack(attacks[0])
}

export function getAttackFromStep(step, attackIndex = 0) {
  return step?.attacks[attackIndex]
}

export function getTaskFromSummary(summary) {
  if (!summary) return

  return Object.values(summary?.taskSummaries)[0]
}

export function scenarioEditedSinceRun(scenario, run) {
  if (!run) {
    return true
  }
  return run.scenario_edit_counter_at_run_time < scenario.edit_counter
}

export function getTargetTypeAndCommand(scenario) {
  const firstAttack = getFirstAttackAsStep(scenario)
  if (!firstAttack) return {}
  const command = firstAttack.impactDefinition.commandType
  const targetType = firstAttack.targetDefinition.targetType
  return { targetType, command }
}

export function getScenarioCommands(scenario) {
  return unique(
    getGraphNodesByType(scenario.graph, ScenarioGraphNodeType.InfraAttack).map(
      (node) => node.impact_definition.infra_command_args.cli_args[0]
    )
  )
}

export function getScenarioTargetTypes(scenario) {
  return unique(
    getGraphNodesByType(scenario.graph, ScenarioGraphNodeType.InfraAttack).map(
      (node) => node.target_definition.target_type
    )
  )
}

export function getBlastRadiusForStep(step) {
  const attack = getAttackFromStep(step)
  const isHost = 'Host' === attack?.targetDefinition?.targetType ?? 'Host'
  const blastRadiusLabel = attack.targetDefinition.strategy.percentage
    ? `${attack.targetDefinition.strategy.percentage}%`
    : attack.targetDefinition.strategy.count

  const blastRadiusValue = targetOverviewSubtitle(
    isHost,
    attack.targetDefinition.strategy
  )

  return {
    blastRadiusLabel,
    blastRadiusValue
  }
}

export function getBlastRadiusForNode(node) {
  const attack = node
  const isHost = 'Host' === attack?.target_definition?.target_type ?? 'Host'
  const strategy = attack.target_definition.strategy
  const blastRadiusLabel = strategy.percentage
    ? `${strategy.percentage}%`
    : strategy.count

  const blastRadiusValue = targetOverviewSubtitle(isHost, strategy)

  return {
    blastRadiusLabel,
    blastRadiusValue
  }
}

export function targetOverviewSubtitle(isHost, strategy) {
  const prefix = strategy.percentage ? 'of selected' : ''
  const suffix = strategy.percentage ? '' : 'selected'
  const type = isHost ? 'hosts' : 'containers'
  return `${prefix} ${type} ${suffix}`
}

export function targetCreationSubtitle(
  isHost,
  activeClients,
  selectedTargetParams,
  targetAll
) {
  const selectedTargetParamsSets = convertParamsToSets(selectedTargetParams)

  return `of ${getTotalSelectedClients(
    activeClients,
    'Random',
    isHost ? 'Hosts' : 'Containers',
    [],
    selectedTargetParamsSets,
    targetAll
  )}`
}

export const sortScenarioListDesc = (scenarios) =>
  naturalSortBy(
    scenarios,
    ({ last_run_at, created_at }) => last_run_at || created_at,
    'desc'
  )
export const filterScenariosByCommands = (scenarios, search = []) => {
  if (search.length === 0) return scenarios

  return scenarios.filter((scenario) => {
    const command =
      Object.values(scenario.graph?.nodes ?? {}).map((node) =>
        node?.impact_definition?.infra_command_type?.toLowerCase()
      ) ?? []

    const matchesCommand = intersection(command, search).length > 0
    if (matchesCommand) {
      return true
    }

    return false
  })
}

export const filterScenariosByTechCategories = (scenarios, search = []) => {
  if (search.length === 0) return scenarios
  return scenarios.filter((scenario) => {
    const techCategory = scenario?.multi_select_tags?.techCategory ?? []

    const matchesCategory = intersection(techCategory, search).length > 0
    if (matchesCategory) {
      return true
    }
    return false
  })
}

export const filterScenarios = (scenarios, search) => {
  const lowercaseSearch = search.toLowerCase().trim()
  return scenarios.filter((scenario) => {
    const matchesName = String(scenario.name)
      .toLowerCase()
      .includes(lowercaseSearch)
    if (matchesName) {
      return true
    }

    const command =
      Object.values(scenario.graph?.nodes ?? {}).map((node) =>
        node?.impact_definition?.infra_command_type?.toLowerCase()
      ) ?? []

    const matchesCommand = command.includes(lowercaseSearch)
    if (matchesCommand) {
      return true
    }

    const matchesAuthor = String(scenario.created_by)
      .toLowerCase()
      .includes(lowercaseSearch)
    if (matchesAuthor) {
      return true
    }

    const matchesDescription = String(scenario.description)
      .toLowerCase()
      .includes(lowercaseSearch)
    if (matchesDescription) {
      return true
    }

    return false
  })
}

export const ACTIVE_RUN_STAGES = ['Setup', 'Active']
export const ERROR_RUN_STAGES = ['SetupFailed', 'Failed']
export const COMPLETED_RUN_STAGES = ['Halted', 'Completed']

export const getRunStage = (run) => run?.stage_info?.stage

export const isRunInActiveStage = (run) =>
  ACTIVE_RUN_STAGES.includes(getRunStage(run))

export const isRunInCompletedStage = (run) =>
  isRunInErrorStage(run) || COMPLETED_RUN_STAGES.includes(getRunStage(run))

export const isRunInErrorStage = (run) =>
  ERROR_RUN_STAGES.includes(getRunStage(run))

export const isRunHalting = (run) => getRunStage(run) === 'Halted'

export const hasBeenRun = (scenario) => Boolean(scenario.last_run_at)

export const getRunStageMetadata = (run) => run?.stage_info?.stageMetadata

export const getTargetingStrategy = (targetType, target) => {
  const targetingStrategy = {
    attrs: {
      //tags and labels go here
    }
  }
  if (target.targetAll) {
    if (targetType === 'Hosts') {
      targetingStrategy.all_hosts = true
    } else if (targetType === 'Containers') {
      targetingStrategy.all_containers = true
    }
  } else if (!isTargetParamsEmpty(target.targetParams)) {
    // If target params are not empty, add them using appropriate key
    targetingStrategy.attrs[
      getTargetParamsKey(targetType, target.targetingStrategyType)
    ] = target.targetParams
  }

  if (target.targetingStrategyType === InfraStrategyType.Random) {
    if (target.percentage) {
      targetingStrategy.type = 'RandomPercent'
      targetingStrategy.percentage = target.percentage
    } else {
      targetingStrategy.type = 'RandomCount'
      targetingStrategy.count = target.exact
    }
  } else {
    targetingStrategy.type = 'Exact'
  }

  return targetingStrategy
}

const getTargetType = (targetType: TargetType) => {
  if (targetType === TargetType.Hosts) {
    return 'Host'
  }
  if (targetType === TargetType.Containers) {
    return 'Container'
  }
  return targetType
}

export function createServiceAttackFromForm(
  { target, impact },
  attackConfiguration
) {
  const command = {
    infra_command_type: impact.type,
    infra_command_args: {
      ...attackConfiguration.command.infraCommandArgs,
      cli_args: [
        impact.type,
        ...(impact?.args?.map((arg) => arg.toString()) ?? [])
      ],
      providers: impact.providers,
      trafficImpactMapping: impact.trafficImpactMapping
        ? cleanObjKeysAndValues(impact.trafficImpactMapping, {
            keyTransformers: [snakeCase]
          })
        : undefined
    }
  }

  const attack = {
    attack_configuration: {
      ...keysToSnakeCase(attackConfiguration),
      command
    },
    include_new_targets: Boolean(target.includeNewTargets),
    type: ScenarioGraphNodeType.InfraAttack
  }

  return attack
}

export const createAttackFromForm = (
  targetType: TargetType,
  { target, impact },
  targets,
  metadata
) => {
  const isKubernetes = targetType === TargetType.Kubernetes
  const hasIncludeNewTargets = target.hasOwnProperty('includeNewTargets')

  const command = {
    cliArgs: [
      impact.type,
      ...(impact?.args?.map((arg) => arg.toString()) ?? [])
    ],
    providers: impact.providers,
    trafficImpactMapping: impact.trafficImpactMapping
      ? cleanObjKeysAndValues(impact.trafficImpactMapping, {
          keyTransformers: [snakeCase]
        })
      : undefined
  }

  const attackConfiguration = generateAttackConfiguration(
    targetType,
    { target, impact },
    metadata,
    targets
  )

  const attack = {
    attack_configuration: attackConfiguration,
    target_definition: {
      target_type: getTargetType(targetType),
      strategy_type: target.targetingStrategyType,
      strategy: getTargetingStrategy(targetType, target)
    },
    impact_definition: {
      infra_command_type: impact.type,
      infra_command_args: {
        ...cleanObjKeysAndValues(command, {
          keysToRemove: ['commandType'],
          keyTransformers: [snakeCase]
        }),
        type: impact.type
      }
    },
    type: ScenarioGraphNodeType.InfraAttack
  }

  if (hasIncludeNewTargets) {
    attack.include_new_targets = Boolean(target.includeNewTargets)
  }

  if (isKubernetes) {
    attack.target_definition.k8s_objects = getSelectedKubernetesTargets(
      targets,
      target
    ).map((k8sTarget) =>
      cleanObjKeysAndValues(k8sTarget, {
        keyTransformers: [snakeCase]
      })
    )
    if (impact.containerSelection) {
      attack.target_definition.containerSelection = {
        selectionType: impact.containerSelection.selectionType.toUpperCase(),
        containerNames: [...impact.containerSelection.containerNames]
      }
    }
  }

  return attack
}

export function getStatusCheckDetails(statusCheckNode: StatusCheckNode) {
  const details = {
    color: null,
    icon: null,
    text: null
  }

  if (statusCheckNode?.state?.evaluation_results?.passed) {
    details.color = theme.colors.green[0]
    details.icon = 'solidCheck'
    details.text = 'Passed'
  } else {
    details.color = theme.colors.red[0]
    details.icon = 'solidClose'
    details.text = 'Failed'
  }

  return {
    ...details
  }
}
